import { forwardRef } from 'react';

import { Accordion as BaseAccordion } from '@base-ui/react/accordion';
import { cn } from 'components/utils';

import {
  accordionHeaderRecipe,
  accordionIconRecipe,
  accordionItemRecipe,
  accordionPanelContentRecipe,
  accordionPanelRecipe,
  accordionRootRecipe,
  accordionTitleRecipe,
  accordionTriggerRecipe,
} from './Accordion.recipe';

import type { AccordionItemProps, AccordionProps } from './types';

/**
 * The chevron, transcribed from the same vector `Select`'s caret uses — node I498:41227, a 10x5
 * stroke from (3,6) to (13,6) in a 16px box at 1.5px.
 *
 * Reusing the shape rather than drawing a second one is the point: a caret that means "this opens"
 * should be one glyph across the system, and the two components are the only places that draw one.
 * It is copied rather than imported for the reason `Select` gives beside its own copy — HNS-262
 * will likely replace both with a library import, and until it lands neither component owns the
 * other's icon.
 *
 * `aria-hidden` and `focusable="false"`: the open state is announced by `aria-expanded` on the
 * trigger, so a second announcement here would be noise, and IE-era SVGs are tab stops without the
 * second attribute.
 */
const CaretDownIcon = ({ className }: { className?: string }) => (
  <svg
    aria-hidden="true"
    className={className}
    fill="none"
    focusable="false"
    height="16"
    viewBox="0 0 16 16"
    width="16"
  >
    <path d="M3 6L8 11L13 6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
  </svg>
);

/**
 * Design System 2.0 accordion — a list of titled sections that expand and collapse.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer,
 * so the same markup is correct in both themes, and `.ds-accordion` needs no dark rule at all to
 * achieve it.
 *
 * ── What this component is NOT ────────────────────────────────────────────────────────────────
 *
 * It is not a tab set, and the difference is keyboard behaviour rather than appearance. Arrow keys
 * do nothing here: every row is an ordinary tab stop, and moving between them is Tab. That is the
 * accordion pattern, and it is also what Base UI 1.7.0 now implements — the APG guidance that
 * once put roving focus on an accordion was withdrawn, and the library's `orientation` and
 * `loopFocus` props are deprecated no-ops as a result. `types.ts` drops both rather than exposing
 * a knob that turns nothing.
 *
 * It is also not a way to hide content from the page while keeping it in the DOM. A collapsed
 * panel is UNMOUNTED, which is what `ExpandableBox` does and what callers of it expect: content
 * behind a closed section does not appear in a find-in-page, does not run its effects, and does
 * not make its network requests. Base UI can do the opposite through `keepMounted` and
 * `hiddenUntilFound`; both are dropped from this component's props, because a caller flipping one
 * would silently change all three of those properties for everything inside.
 *
 * ── Single or multiple open ───────────────────────────────────────────────────────────────────
 *
 * `multiple` is the caller's choice and is forwarded untouched, because a headless accordion
 * supports both at no cost. The default is Base UI's: one open section at a time. Note what single
 * mode does NOT do — there is no "one must stay open" behaviour, so pressing the open row's
 * trigger collapses it and leaves the list with nothing open. That is the pattern's normal
 * behaviour rather than an omission; a caller needing a section always open owns `value`.
 */
export const Accordion = ({ children, className, iconPlacement = 'end', ...rest }: AccordionProps) => (
  // `data-icon-placement` is read by a selector in the icon's recipe rather than by any code here,
  // which is what keeps a purely visual choice out of the component. It is set unconditionally so
  // the attribute is something a test can read for either value, not only for the non-default one.
  <BaseAccordion.Root className={cn(accordionRootRecipe(), className)} data-icon-placement={iconPlacement} {...rest}>
    {children}
  </BaseAccordion.Root>
);

/**
 * One titled section.
 *
 * A separate exported component rather than a `sections` array prop, so the content between the
 * tags is ordinary JSX — the whole requirement is "arbitrary content", and an array of render
 * functions is a worse way to write arbitrary content than children are.
 *
 * The four parts are assembled here rather than left to the caller because their relationship is
 * what makes the thing accessible: the trigger is a real `<button>` inside Base UI's `<h3>`, so
 * the section appears in the document's heading outline and a screen reader can jump to it; the
 * button carries `aria-expanded`, and `aria-controls` pointing at the panel WHILE OPEN — Base UI
 * omits it when closed, since the panel it would name is not in the DOM to be pointed at; and the
 * panel is a `role="region"` labelled by the trigger. None of that is something a caller should
 * have to reassemble correctly.
 *
 * `data-part` names each element so a parent can target it and a test can assert on it without
 * depending on a styling class, the same convention the field pair uses for its box.
 */
export const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ children, className, panelClassName, title, triggerClassName, ...rest }, ref) => (
    <BaseAccordion.Item ref={ref} className={cn(accordionItemRecipe(), className)} data-part="item" {...rest}>
      <BaseAccordion.Header className={accordionHeaderRecipe()}>
        <BaseAccordion.Trigger className={cn(accordionTriggerRecipe(), triggerClassName)} data-part="trigger">
          <span className={accordionTitleRecipe()}>{title}</span>
          <CaretDownIcon className={accordionIconRecipe()} />
        </BaseAccordion.Trigger>
      </BaseAccordion.Header>

      {/* The padding lives on the inner wrapper, not on the panel — see `accordionPanelRecipe`. */}
      <BaseAccordion.Panel className={accordionPanelRecipe()} data-part="panel">
        <div className={cn(accordionPanelContentRecipe(), panelClassName)}>{children}</div>
      </BaseAccordion.Panel>
    </BaseAccordion.Item>
  )
);
